import { AnimatedSection } from "./AnimatedSection"
import { FloatingCard } from "./FloatingCard"
import { Clock, Euro, TrendingDown, Users, BarChart3, Target, XCircle, CheckCircle2, Upload, ThumbsUp, MessageSquare } from "lucide-react"

export function AmplifySection() {
  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="container px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-16">
          
          {/* Headline + Subheadline */}
          <AnimatedSection direction="up" className="text-center space-y-4">
            <h2 className="text-3xl lg:text-5xl font-bold font-playfair leading-tight">
              Wir haben gemessen: Der durchschnittliche Post kostet Sie{" "}
              <span className="text-primary">47 Minuten.</span>
            </h2>
            <p className="text-xl lg:text-2xl text-muted-foreground">
              Von der Idee bis zur Veröffentlichung. Das sind 40 Stunden pro Monat für 50 Posts.
            </p>
            <p className="text-lg lg:text-xl text-muted-foreground font-semibold">
              Unsere Analyse von 1.000+ Geschäftsinhabern zeigt die versteckten Kosten.
            </p>
          </AnimatedSection>

          {/* Two-Column Layout */}
          <AnimatedSection delay={100} direction="up">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              
              {/* SPALTE 1: Der Zeit-Breakdown */}
              <div className="space-y-8" data-testid="column-time-breakdown">
                <h3 className="text-2xl font-bold font-playfair text-center lg:text-left">
                  Der Zeit-Breakdown
                </h3>
                
                <div className="space-y-6">
                  {/* 47 Minuten pro Post */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Clock className="w-8 h-8 text-primary flex-shrink-0" />
                      <div>
                        <p className="text-4xl lg:text-5xl font-bold text-primary">47 Minuten</p>
                        <p className="text-base text-muted-foreground">pro Post</p>
                      </div>
                    </div>
                    <div className="ml-11 space-y-1 text-sm text-foreground">
                      <p>↳ 12 Min: "Was soll ich posten?"</p>
                      <p>↳ 18 Min: Schreiben & Umschreiben</p>
                      <p>↳ 9 Min: Bild suchen/erstellen</p>
                      <p>↳ 8 Min: Formatieren & Posten</p>
                    </div>
                  </div>

                  {/* 520 Stunden pro Jahr */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="w-8 h-8 text-primary flex-shrink-0" />
                      <div>
                        <p className="text-4xl lg:text-5xl font-bold text-primary">520 Stunden</p>
                        <p className="text-base text-muted-foreground">pro Jahr</p>
                      </div>
                    </div>
                    <p className="ml-11 text-sm text-muted-foreground">
                      Bei 50 Posts/Monat × 12 Monate
                    </p>
                  </div>

                  {/* €52.000 Opportunity Cost */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Euro className="w-8 h-8 text-primary flex-shrink-0" />
                      <div>
                        <p className="text-4xl lg:text-5xl font-bold text-primary">€52.000</p>
                        <p className="text-base text-muted-foreground">Opportunity Cost</p>
                      </div>
                    </div>
                    <p className="ml-11 text-sm text-muted-foreground">
                      Bei €100 Stundensatz (konservativ)
                    </p>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground italic">
                  Basierend auf Time-Motion Studies von 1.000+ KMU-Inhabern in D-A-CH Region, Q4 2024
                </p>
              </div>

              {/* SPALTE 2: Das Ergebnis */}
              <div className="space-y-8" data-testid="column-results">
                <h3 className="text-2xl font-bold font-playfair text-center lg:text-left">
                  Das Ergebnis
                </h3>
                
                <div className="space-y-6">
                  {/* 73% organische Reichweite verloren */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <TrendingDown className="w-8 h-8 text-destructive flex-shrink-0" />
                      <div>
                        <p className="text-4xl lg:text-5xl font-bold text-destructive">73%</p>
                        <p className="text-base text-muted-foreground">organische Reichweite verloren</p>
                      </div>
                    </div>
                    <p className="ml-11 text-sm text-muted-foreground">
                      LinkedIn Algorithmus bestraft Inkonsistenz
                    </p>
                  </div>

                  {/* 127 Impressions im Durchschnitt */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Users className="w-8 h-8 text-destructive flex-shrink-0" />
                      <div>
                        <p className="text-4xl lg:text-5xl font-bold text-destructive">127</p>
                        <p className="text-base text-muted-foreground">Impressions im Durchschnitt</p>
                      </div>
                    </div>
                    <p className="ml-11 text-sm text-muted-foreground">
                      Pro Post, ohne Paid Boost
                    </p>
                  </div>

                  {/* €47 pro Impression */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Target className="w-8 h-8 text-destructive flex-shrink-0" />
                      <div>
                        <p className="text-4xl lg:text-5xl font-bold text-destructive">€47</p>
                        <p className="text-base text-muted-foreground">pro Impression</p>
                      </div>
                    </div>
                    <p className="ml-11 text-sm text-muted-foreground">
                      Ihre Zeit-Kosten / Reichweite<br />
                      (Eine Google Ad: €0,50)
                    </p>
                  </div>

                  {/* 0 neue Kunden */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <XCircle className="w-8 h-8 text-destructive flex-shrink-0" />
                      <div>
                        <p className="text-4xl lg:text-5xl font-bold text-destructive">0</p>
                        <p className="text-base text-muted-foreground">neue Kunden</p>
                      </div>
                    </div>
                    <p className="ml-11 text-sm text-muted-foreground">
                      Ohne System, keine Resultate
                    </p>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground italic">
                  LinkedIn Platform Analytics, 2024. Google Ads Benchmark: €0,35-€0,65 CPC
                </p>
              </div>
            </div>
          </AnimatedSection>

          {/* Featured Panel 1: Die Rechnung */}
          <AnimatedSection delay={200} direction="up">
            <FloatingCard className="bg-accent/5 p-8 lg:p-10" data-testid="panel-calculation">
              <h3 className="text-2xl lg:text-3xl font-bold font-playfair mb-8 text-center">
                Die Rechnung
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                {/* VORHER */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <XCircle className="w-6 h-6 text-destructive" />
                    <h4 className="text-xl font-bold text-destructive">VORHER (Ohne A-Team)</h4>
                  </div>
                  <div className="space-y-2 text-sm lg:text-base ml-2">
                    <p>├─ <span className="font-semibold">Zeit:</span> 40h/Monat = 480h/Jahr</p>
                    <p>├─ <span className="font-semibold">Kosten:</span> €48.000/Jahr (Opportunity Cost)</p>
                    <p>├─ <span className="font-semibold">Output:</span> 50 Posts/Monat, inkonsistent</p>
                    <p>└─ <span className="font-semibold">Ergebnis:</span> Mittelmäßig</p>
                  </div>
                </div>

                {/* NACHHER */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-6 h-6 text-primary" />
                    <h4 className="text-xl font-bold text-primary">NACHHER (Mit Linas A-Team)</h4>
                  </div>
                  <div className="space-y-2 text-sm lg:text-base ml-2">
                    <p>├─ <span className="font-semibold">Zeit:</span> 2h/Monat = 24h/Jahr</p>
                    <div className="ml-4 space-y-1 text-sm text-muted-foreground">
                      <p>↳ Fotos hochladen: 30 Min</p>
                      <p>↳ Posts freigeben: 30 Min</p>
                      <p>↳ Kontext geben: 60 Min</p>
                    </div>
                    <p>├─ <span className="font-semibold">Kosten:</span> €2.988/Jahr (249€/Monat)</p>
                    <p>├─ <span className="font-semibold">Output:</span> 60 Posts/Monat, automatisch</p>
                    <p>└─ <span className="font-semibold">Ergebnis:</span> Professionell & konsistent</p>
                  </div>
                </div>
              </div>

              {/* Savings Highlight */}
              <div className="mt-8 pt-8 border-t">
                <p className="text-center text-lg lg:text-xl font-bold">
                  Sie sparen:{" "}
                  <span className="text-primary text-2xl lg:text-3xl">456 Stunden + €45.012</span>
                  {" "}= 57 Arbeitstage
                </p>
              </div>
            </FloatingCard>
          </AnimatedSection>

          {/* Featured Panel 2: Die ehrliche Realität */}
          <AnimatedSection delay={250} direction="up">
            <FloatingCard className="bg-accent/5 p-8 lg:p-10" data-testid="panel-reality">
              <h3 className="text-2xl lg:text-3xl font-bold font-playfair mb-8 text-center">
                Die ehrliche Realität
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Sie investieren */}
                <div className="space-y-4">
                  <h4 className="text-xl font-bold text-primary mb-4">Sie investieren:</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Upload className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">30 Min/Monat: Fotos hochladen</p>
                        <p className="text-sm text-muted-foreground">(2 Min × 15 Uploads)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <ThumbsUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">30 Min/Monat: Posts freigeben</p>
                        <p className="text-sm text-muted-foreground">(30 Sek × 60 Posts)*</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MessageSquare className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">60 Min/Monat: Interviews/Kontext</p>
                        <p className="text-sm text-muted-foreground">für spezielle Posts</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-primary/10 rounded-lg">
                    <p className="text-lg font-bold text-center">
                      = 2 Stunden/Monat statt 40 Stunden/Monat
                    </p>
                    <p className="text-2xl font-bold text-primary text-center mt-2">
                      = 95% Zeitersparnis
                    </p>
                  </div>

                  <p className="text-xs text-muted-foreground italic mt-4">
                    *Optional: Aktivieren Sie Auto-Posting und investieren Sie 0 Minuten für Freigaben
                  </p>
                </div>

                {/* Ihr Team arbeitet */}
                <div className="space-y-4">
                  <h4 className="text-xl font-bold text-primary mb-4">Ihr Team arbeitet:</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <p>24/7 Content-Erstellung</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <p>Plattform-Optimierung</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <p>Performance-Tracking</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      <p>Strategie-Anpassung</p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-primary/10 rounded-lg">
                    <p className="text-lg font-bold text-center">Kosten:</p>
                    <p className="text-3xl lg:text-4xl font-bold text-primary text-center mt-2">
                      249€/Monat
                    </p>
                    <p className="text-sm text-muted-foreground text-center mt-2">
                      (Weniger als eine Stunde Ihrer Zeit wert ist)
                    </p>
                  </div>
                </div>
              </div>
            </FloatingCard>
          </AnimatedSection>

          {/* Closing Statement */}
          <AnimatedSection delay={300} direction="up">
            <div className="text-center space-y-4">
              <p className="text-xl lg:text-2xl font-bold font-playfair text-balance max-w-4xl mx-auto">
                Die Frage ist nicht, ob Sie sich ein Social Media Team leisten können.
              </p>
              <p className="text-2xl lg:text-3xl font-bold text-primary font-playfair text-balance max-w-4xl mx-auto">
                Die Frage ist: Können Sie es sich leisten, weiterhin ohne eines zu arbeiten?
              </p>
            </div>
          </AnimatedSection>

        </div>
      </div>
    </section>
  )
}
